import { format, intlFormat, isValid, parseISO } from 'date-fns';

import { GoalReport } from '@data';

import { CustomRangeInput, GoalFilters } from './GoalReportContext';

type ReportPeriod = {
    legend: string;
    value: string;
};

export function getReportPeriod(swings?: GoalReport['swings']): ReportPeriod {
    const period = getReportPeriodDates(swings);
    if (!period) {
        return {
            legend: 'Report Period',
            value: 'No activity yet',
        };
    }

    const formatDate = (date: Date) =>
        intlFormat(date, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

    if (period.from.toDateString() === period.to.toDateString()) {
        return {
            legend: 'Report Day',
            value: formatDate(period.from),
        };
    }

    return {
        legend: 'Report Period',
        value: `${formatDate(period.from)} – ${formatDate(period.to)}`,
    };
}

type DataIncluded = {
    legend: string;
    value: string;
    activityCount: number;
    swingCount: number;
};

export function getGoalDataIncluded(swings?: GoalReport['swings']): DataIncluded {
    const swingCount = swings?.length ?? 0;

    if (!swings || swingCount === 0) {
        return {
            legend: 'Based on',
            value: 'No swings added to this report yet',
            activityCount: 0,
            swingCount: 0,
        };
    }

    const activityIds = new Set(swings.map((s) => s.activityId));
    const activityCount = activityIds.size;

    return {
        legend: 'Based on',
        value: `${activityCount} ${activityCount === 1 ? 'activity' : 'activities'} (${swingCount} ${
            swingCount === 1 ? 'swing' : 'swings'
        })`,
        activityCount,
        swingCount,
    };
}

export function getReportPeriodDates(swings?: GoalReport['swings']): null | { from: Date; to: Date } {
    if (!swings || swings.length === 0) {
        return null;
    }

    const dates = swings
        .map((swing) => parseISO(swing.createdAt))
        .filter(isValid)
        .sort((a, b) => a.getTime() - b.getTime());

    if (dates.length === 0) {
        return null;
    }

    const from = dates[0];
    const to = dates[dates.length - 1];

    return {
        from,
        to,
    };
}

export function getFilterDescription(filter: Partial<GoalFilters>, customRange: CustomRangeInput) {
    const swingMap = {
        all: 'all',
        last6: 6,
        last10: 10,
        last20: 20,
    };

    if (filter.period === 'custom') {
        const { from, to } = customRange;

        if (!from || !to) {
            return 'Showing swings in a custom date range';
        }

        const fromDate = format(parseISO(from), 'MMM d, yyyy');
        const toDate = format(parseISO(to), 'MMM d, yyyy');

        return `Period: ${fromDate} – ${toDate}`;
    }

    if (filter.period && filter.period !== 'all') {
        return `Period: Last ${filter.period}`;
    }

    if (filter.swings && filter.swings !== 'all') {
        return `Swings: Last ${swingMap[filter.swings]}`;
    }

    return 'All swings';
}
